import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import EmployerGuard from '../components/EmployerGuard';
import NotificationBell from '../components/NotificationBell';

const STATUS_COLOR = {
  active:           { bg:'#f0fdf4', color:'#166534', label:'Active' },
  pending_payment:  { bg:'#fefce8', color:'#854d0e', label:'Pending Payment' },
  pending_approval: { bg:'#fff7ed', color:'#9a3412', label:'Pending Approval' },
  paused:           { bg:'#f1f5f9', color:'#475569', label:'Paused' },
  closed:           { bg:'#f8fafc', color:'#64748b', label:'Closed' },
  expired:          { bg:'#fef2f2', color:'#991b1b', label:'Expired' },
  rejected:         { bg:'#fef2f2', color:'#991b1b', label:'Rejected' },
  draft:            { bg:'#f8fafc', color:'#64748b', label:'Draft' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg:'#f8fafc', color:'#64748b', label: status };
  return (
    <span style={{ background:s.bg, color:s.color, padding:'3px 10px', borderRadius:999, fontSize:11, fontWeight:700 }}>
      {s.label}
    </span>
  );
}

function DashboardContent({ auth }) {
  const navigate = useNavigate();
  const { user, token, logout } = auth;

  const [jobs, setJobs]               = useState([]);
  const [apps, setApps]               = useState([]);
  const [notifs, setNotifs]           = useState([]);
  const [unread, setUnread]           = useState(0);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      jbApi.employerJobs(token),
      jbApi.employerApplications(token),
      jbApi.notifications(token),
      jbApi.unreadCount(token),
    ]).then(([j, a, n, u]) => {
      setJobs(j.jobs || []);
      setApps(a.applications || []);
      setNotifs((n.notifications || []).slice(0, 6));
      setUnread(u.unread_count || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  // Computed stats
  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const totalViews = jobs.reduce((s, j) => s + (j.view_count || 0), 0);
  const totalApps  = jobs.reduce((s, j) => s + (j.application_count || 0), 0);
  const pendingReview = apps.filter(a => a.status === 'applied').length;

  function markAllRead() {
    jbApi.markAllRead(token).then(() => {
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    });
  }

  return (
    <div className="jb-page" data-testid="employer-dashboard">
      {/* ── Portal Header ── */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:11, color:'rgba(245,240,232,.6)', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', marginBottom:2 }}>Employer Portal</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8' }}>{user?.company_name || 'Your Company'}</div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <NotificationBell token={token} />
            <Link to="/career-hub/post" data-testid="post-job-btn" style={{
              background:'#C9A84C', color:'#0D2137', padding:'8px 18px',
              borderRadius:8, fontSize:13, fontWeight:800, textDecoration:'none',
            }}>
              + Post Job
            </Link>
            <button onClick={logout} style={{ background:'rgba(255,255,255,.1)', border:'none', color:'rgba(245,240,232,.7)', padding:'8px 14px', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #ddd8cc' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', gap:4 }}>
          {[
            { label:'Dashboard', to:'/employer/dashboard' },
            { label:'My Listings', to:'/employer/jobs' },
            { label:'Profile', to:'/employer/profile' },
          ].map(({ label, to }) => (
            <Link key={to} to={to} style={{
              padding:'14px 16px', fontSize:13, fontWeight:600,
              color: window.location.pathname === to ? '#1B6B72' : '#5a6373',
              borderBottom: window.location.pathname === to ? '2px solid #1B6B72' : '2px solid transparent',
              textDecoration:'none', whiteSpace:'nowrap',
            }}>{label}</Link>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth:1100, margin:'0 auto', padding:'28px 24px 60px' }}>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:'#5a6373' }}>Loading dashboard…</div>
        ) : (
          <>
            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16, marginBottom:28 }} data-testid="dashboard-stats">
              {[
                { label:'Active Listings', value: activeJobs, color:'#1B6B72' },
                { label:'Total Applications', value: totalApps, color:'#0D2137' },
                { label:'Total Views', value: totalViews, color:'#0D2137' },
                { label:'Pending Review', value: pendingReview, color: pendingReview > 0 ? '#c0392b' : '#0D2137' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background:'#fff', borderRadius:10, padding:'18px 20px', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
                  <div style={{ fontSize:28, fontWeight:800, color, fontFamily:'Inter,sans-serif', marginBottom:4 }}>{value}</div>
                  <div style={{ fontSize:12, color:'#5a6373', fontWeight:500 }}>{label}</div>
                </div>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:24 }}>

              {/* Recent Listings */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                  <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0D2137' }}>Recent Listings</h2>
                  <Link to="/employer/jobs" style={{ fontSize:12, color:'#1B6B72', fontWeight:600, textDecoration:'none' }}>View All →</Link>
                </div>

                {jobs.length === 0 ? (
                  <div style={{ background:'#fff', borderRadius:10, padding:'40px 20px', textAlign:'center', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
                    <div style={{ fontSize:13, color:'#5a6373', marginBottom:14 }}>You haven't posted any jobs yet.</div>
                    <Link to="/career-hub/post" style={{ background:'#1B6B72', color:'#fff', padding:'10px 20px', borderRadius:8, fontSize:13, fontWeight:700, textDecoration:'none' }}>Post Your First Job →</Link>
                  </div>
                ) : (
                  jobs.slice(0, 5).map(job => (
                    <div key={job.id} style={{ background:'#fff', borderRadius:10, padding:'14px 18px', marginBottom:10, boxShadow:'0 2px 8px rgba(13,33,55,.07)', display:'flex', alignItems:'center', gap:14 }} data-testid={`dashboard-job-${job.id}`}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:700, color:'#0D2137', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{job.title}</div>
                        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                          <StatusBadge status={job.status} />
                          <span style={{ fontSize:11, color:'#5a6373' }}>{job.view_count || 0} views · {job.application_count || 0} apps</span>
                        </div>
                      </div>
                      <Link to={`/employer/jobs/${job.id}/applicants`} style={{ fontSize:12, color:'#1B6B72', fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}>
                        View Applicants →
                      </Link>
                    </div>
                  ))
                )}
              </div>

              {/* Notifications */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                  <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0D2137' }}>
                    Notifications {unread > 0 && <span style={{ background:'#c0392b', color:'#fff', borderRadius:999, fontSize:10, padding:'2px 7px', marginLeft:6 }}>{unread}</span>}
                  </h2>
                  {unread > 0 && <button onClick={markAllRead} style={{ fontSize:11, color:'#1B6B72', background:'none', border:'none', cursor:'pointer', fontWeight:600, fontFamily:'Inter,sans-serif', padding:0 }}>Mark all read</button>}
                </div>

                <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 2px 8px rgba(13,33,55,.07)', overflow:'hidden' }}>
                  {notifs.length === 0 ? (
                    <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>No notifications yet</div>
                  ) : (
                    notifs.map(n => (
                      <div key={n.id} style={{ padding:'10px 14px', borderBottom:'1px solid #f5f0e8', background: n.is_read ? '#fff' : 'rgba(27,107,114,.04)' }} data-testid={`notif-${n.id}`}>
                        <div style={{ fontSize:12, fontWeight: n.is_read ? 500 : 700, color:'#0D2137', marginBottom:2 }}>{n.title}</div>
                        <div style={{ fontSize:11, color:'#5a6373' }}>{n.message}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function EmployerDashboardPage() {
  const auth = useJBAuth();
  return <EmployerGuard><DashboardContent auth={auth} /></EmployerGuard>;
}
